/**
 * Redux action creators for user settings, groups, and password management.
 *
 * @module redux/settings/actions
 * @author Snowfly
 * @version 1.0.0
 */
import axios from 'axios';
import {
    FETCH_REQUEST,
    FETCH_FAILURE,
    FETCH_GROUPS,
    SAVE_SETTINGS,
    UPDATE_PASSWORD,
} from './settingsType';
import config from '../../config';

/**
 * A function that constructs and returns an action object
 * to represent the fetching request action.
 *
 * @function fetchRequest
 * @returns {Object} An action object containing the action type.
 */
export const fetchRequest = () => ({
    type: FETCH_REQUEST,
});

/**
 * Action creator for handling a failed fetch request.
 *
 * This function generates an action object used to signify the failure of
 * a fetch operation. It dispatches the failure type along with the failed data.
 *
 * @param {any} data - The payload containing details about the failure.
 * @returns {Object} The action object with `type` set to `FETCH_FAILURE` and `payload` containing the `data`.
 */
export const fetchFailure = (data) => ({
    type: FETCH_FAILURE,
    payload: data,
});

/**
 * Action creator function for fetching groups.
 *
 * This function constructs an action with a type of `FETCH_GROUPS` and a payload containing
 * the provided data. It is used to trigger the fetch groups action within a Redux workflow.
 *
 * @param {Object} data - The payload data to be included in the action. Represents the relevant
 *                        information related to groups that needs to be fetched.
 * @returns {Object} An action object containing the type `FETCH_GROUPS` and the provided payload.
 */
export const fetchGroups = (data) => ({
    type: FETCH_GROUPS,
    payload: data,
});

/**
 * Asynchronous action creator function to fetch a list of groups.
 * Dispatches a `fetchRequest` action before making the API call.
 * If the API call succeeds, dispatches a `fetchGroups` action with the response data.
 * If the API call fails, dispatches a `fetchFailure` action with the error.
 *
 * @function fetchSelectGroups
 * @returns {Function} A thunk function that performs the asynchronous operation.
 */
export const fetchSelectGroups = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(`${config.API_BASE_URL}/api/v1/sims/group/listing`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchGroups(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};
/**
 * Action creator function for saving settings in the application.
 *
 * This function creates and returns an action object with the type `SAVE_SETTINGS`
 * and a payload containing the settings data to be saved.
 *
 * @function saveSettings
 * @param {Object} data - The settings data to be saved.
 * @returns {Object} An action object with `type` and `payload` properties.
 */
export const saveSettings = (data) => ({
    type: SAVE_SETTINGS,
    payload: data,
});
/**
 * Function to handle the change password action.
 *
 * @function changePassword
 * @param {Object} data - The data related to the password change request.
 * @param {Object} res - The response object containing the updated password details or status.
 * @returns {Object} An action object containing the type and payload for updating the password.
 */
export const changePassword = (data, res) => ({
    type: UPDATE_PASSWORD,
    payload: res,
});
/**
 * An asynchronous action creator function used to update the password of a user.
 * Dispatches actions to indicate the status of the password update request.
 *
 * @function
 * @param {Object} data - The data object containing the password update information.
 * @param {string} data.oldPassword - The current password of the user.
 * @param {string} data.newPassword - The new password to be set for the user.
 * @returns {Function} A thunk function that performs the password update process and
 *                     dispatches required actions.
 */
export const updatePassword = (data) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.post(`${config.API_BASE_URL}/api/v1/users/update-password`, data, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(changePassword(data, res)))
        .catch((error) => dispatch(fetchFailure(error)));
};
